/* eslint-disable @typescript-eslint/no-unused-vars */

import { useMemo } from 'react';

import { useSubscription } from '@/api-hooks/useSubscription';
import {
  isAnnualSpecialPlan,
  OfferingType,
  originPlanToKor,
  PLAN,
  PLAN_NAME,
  planToKor,
} from '@/types/plan';

type UsePlanReturnType = {
  planName: PLAN_NAME;
  planImgSrc: string;
  planImgSrcMobile: string;
  planNameWithOriginPlan?: string;
  offeringType: OfferingType;
  originPlan: PLAN;
  isFreeUser: boolean;
  // month
  planPeriod: number;
};

// 요금제 이미지 경로 상수
const PLAN_IMAGES = {
  [PLAN_NAME.Free]: '/img2/illust/plan/free_L.png',
  [PLAN_NAME.FreeTrial]: '/img2/illust/plan/free-trial_L.png',
  [PLAN_NAME.Efficient]: '/img2/illust/plan/efficient_L.png',
  [PLAN_NAME.Standard]: '/img2/illust/plan/standard_L.png',
  [PLAN_NAME.Special]: '/img2/illust/plan/special_L.png',
  [PLAN_NAME.SpecialPlus]: '/img2/illust/plan/special-plus_L.png',
  [PLAN_NAME.Partner]: '/img2/illust/plan/partner_L.png',
  [PLAN_NAME.Franchise]: '/img2/illust/plan/franchise_L.png',
  FREE_NO_PLAN: '/img2/illust/plan/free-trial_L_no-plan.png',
  PARTNER_NO_PLAN: '/img2/illust/plan/partner_L_no-plan.png',
  SPECIAL_ANNUAL: '/img2/illust/plan/special-annual_L.png',
} as const;

const PLAN_IMAGES_MOBILE = {
  [PLAN_NAME.Free]: '/img2/illust/plan/free_S.png',
  [PLAN_NAME.FreeTrial]: '/img2/illust/plan/free-trial_S.png',
  [PLAN_NAME.Efficient]: '/img2/illust/plan/efficient_S.png',
  [PLAN_NAME.Standard]: '/img2/illust/plan/standard_S.png',
  [PLAN_NAME.Special]: '/img2/illust/plan/special_S.png',
  [PLAN_NAME.SpecialPlus]: '/img2/illust/plan/special-plus_S.png',
  [PLAN_NAME.Partner]: '/img2/illust/plan/partner_S.png',
  [PLAN_NAME.Franchise]: '/img2/illust/plan/franchise_S.png',
  FREE_NO_PLAN: '/img2/illust/plan/free-trial_S_no-plan.png',
  PARTNER_NO_PLAN: '/img2/illust/plan/partner_S_no-plan.png',
  SPECIAL_ANNUAL: '/img2/illust/plan/special-annual_S.png',
} as const;

export const useSubscriptionPlan = (): UsePlanReturnType => {
  const { data: subscription } = useSubscription();

  const planName = useMemo(() => {
    if (!subscription?.plan?.name) return PLAN_NAME.Free;

    // offering_type 우선 체크
    if (subscription.plan.offering_type === OfferingType.PROMOTIONAL) {
      return PLAN_NAME.FreeTrial;
    }
    if (subscription.plan.offering_type === OfferingType.PARTNER) {
      return PLAN_NAME.Partner;
    }

    // origin_plan 기반 매핑
    const originPlanNameMap: Record<PLAN, PLAN_NAME> = {
      [PLAN.EFFICIENT]: PLAN_NAME.Efficient,
      [PLAN.STANDARD]: PLAN_NAME.Standard,
      [PLAN.SPECIAL_PLUS]: PLAN_NAME.SpecialPlus,
      [PLAN.SPECIAL]: PLAN_NAME.Special,
      [PLAN.FREE]: PLAN_NAME.Free,
    };

    if (subscription.plan.origin_plan && originPlanNameMap[subscription.plan.origin_plan]) {
      return originPlanNameMap[subscription.plan.origin_plan];
    }

    return PLAN_NAME.Free;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.plan]);

  const planImgSrc = useMemo(() => {
    if (!subscription?.plan) return PLAN_IMAGES[PLAN_NAME.Free];

    // offering_type 우선 체크
    if (subscription.plan.offering_type === OfferingType.PROMOTIONAL) {
      return PLAN_IMAGES.FREE_NO_PLAN;
    }
    if (subscription.plan.offering_type === OfferingType.PARTNER) {
      return PLAN_IMAGES.PARTNER_NO_PLAN;
    }

    // 연간 스페셜(SSOT: RETAIL·special·period>=12) 전용 티켓 이미지 (배민페스타 period2 오인 방지)
    if (isAnnualSpecialPlan(subscription.plan)) {
      return PLAN_IMAGES.SPECIAL_ANNUAL;
    }

    // origin_plan 기반 매핑
    const originPlanMap: Record<PLAN, string> = {
      [PLAN.EFFICIENT]: PLAN_IMAGES[PLAN_NAME.Efficient],
      [PLAN.STANDARD]: PLAN_IMAGES[PLAN_NAME.Standard],
      [PLAN.SPECIAL_PLUS]: PLAN_IMAGES[PLAN_NAME.SpecialPlus],
      [PLAN.SPECIAL]: PLAN_IMAGES[PLAN_NAME.Special],
      [PLAN.FREE]: PLAN_IMAGES[PLAN_NAME.Free],
    };

    if (subscription.plan.origin_plan && originPlanMap[subscription.plan.origin_plan]) {
      return originPlanMap[subscription.plan.origin_plan];
    }

    return PLAN_IMAGES[PLAN_NAME.Free];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.plan]);

  const planImgSrcMobile = useMemo(() => {
    if (!subscription?.plan) return PLAN_IMAGES_MOBILE[PLAN_NAME.Free];

    // offering_type 우선 체크
    if (subscription.plan.offering_type === OfferingType.PROMOTIONAL) {
      return PLAN_IMAGES_MOBILE.FREE_NO_PLAN;
    }
    if (subscription.plan.offering_type === OfferingType.PARTNER) {
      return PLAN_IMAGES_MOBILE.PARTNER_NO_PLAN;
    }

    // 연간 스페셜(SSOT: RETAIL·special·period>=12) 전용 티켓 이미지 (배민페스타 period2 오인 방지)
    if (isAnnualSpecialPlan(subscription.plan)) {
      return PLAN_IMAGES_MOBILE.SPECIAL_ANNUAL;
    }

    // origin_plan 기반 매핑
    const originPlanMapMobile: Record<PLAN, string> = {
      [PLAN.EFFICIENT]: PLAN_IMAGES_MOBILE[PLAN_NAME.Efficient],
      [PLAN.STANDARD]: PLAN_IMAGES_MOBILE[PLAN_NAME.Standard],
      [PLAN.SPECIAL_PLUS]: PLAN_IMAGES_MOBILE[PLAN_NAME.SpecialPlus],
      [PLAN.SPECIAL]: PLAN_IMAGES_MOBILE[PLAN_NAME.Special],
      [PLAN.FREE]: PLAN_IMAGES_MOBILE[PLAN_NAME.Free],
    };

    if (subscription.plan.origin_plan && originPlanMapMobile[subscription.plan.origin_plan]) {
      return originPlanMapMobile[subscription.plan.origin_plan];
    }

    return PLAN_IMAGES_MOBILE[PLAN_NAME.Free];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.plan]);

  const planNameWithOriginPlan = useMemo(() => {
    if (!subscription?.plan?.offering_type) {
      return PLAN_NAME.Free;
    }

    // offering_type 우선 체크
    if (subscription.plan.offering_type === OfferingType.PROMOTIONAL) {
      return `${PLAN_NAME.FreeTrial}(${originPlanToKor[subscription.plan.origin_plan]})`;
    }
    if (subscription.plan.offering_type === OfferingType.PARTNER) {
      return `${PLAN_NAME.Partner}(${originPlanToKor[subscription.plan.origin_plan]})`;
    }
    return originPlanToKor[subscription.plan.origin_plan];
  }, [subscription?.plan]);

  const offeringType = subscription?.plan?.offering_type || OfferingType.RETAIL;
  const originPlan = subscription?.plan?.origin_plan || PLAN.FREE;
  const isFreeUser = planName === PLAN_NAME.Free || offeringType === OfferingType.PROMOTIONAL;

  return {
    planName,
    planNameWithOriginPlan,
    planImgSrc,
    planImgSrcMobile,
    offeringType,
    originPlan,
    isFreeUser,
    planPeriod: subscription?.plan?.period || 1,
  };
};

export default useSubscriptionPlan;
